/**
 * useTenantFeatures — Sprint 45
 *
 * Fetches feature flags for the current user's tenant.
 * Used to conditionally show/hide kacheln and navigation items.
 *
 * Returns:
 *   features: { crop: bool, livestock: bool, energy: bool, ... }
 *   isEnabled: (featureName) => bool
 *   loaded: bool
 */

import { useState, useEffect } from 'react';

const DEFAULT_FEATURES = {
  crop: true,
  livestock: true,
  energy: true,
  direct_sales: true,
  compliance: true,
  market: true,
  // Phase-1 navigation gates — controls tile/tab visibility.
  // Flip to true when Phase 2/3 features are production-ready.
  // Can be overridden per-tenant via /api/v1/aif/tenant/features.
  storage_tile: false,
  finance_tile: false,
  compliance_tile: false,
  reports_tab: false,
  pois_tab: false,
  // UX-P3: map layer gates — strictly hidden until data exists
  buildings_layer: false,
  // Notification-Fix-1: parent-app gates for notification types.
  // When OFF, notification types tied to that app are hidden + not fired.
  contracts_app: false,
  insurance_app: false,
  cashflow_app: false,
  stock_app: false,
};

export function useTenantFeatures(token) {
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    fetch('/api/v1/aif/tenant/features', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data?.features) {
          setFeatures({ ...DEFAULT_FEATURES, ...data.features });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });

    return () => { cancelled = true; };
  }, [token]);

  const isEnabled = (featureName) => features[featureName] !== false;

  return { features, isEnabled, loaded };
}
